/**
 * SDR Stock Manager - Google OAuth & Session Management Pipeline
 * @module services/auth
 */

import { STORAGE_KEYS, SUPABASE_CONFIG, ALLOWED_AUTH_DOMAINS } from '../config/constants.js';

/**
 * In-memory active authentication session.
 * @type {any}
 */
let currentAuthSession = null;

/**
 * In-memory active user object.
 * @type {any}
 */
let currentAuthUser = null;

/**
 * Gets the current in-memory auth session.
 * @returns {any}
 */
export function getCurrentAuthSession() {
  return currentAuthSession;
}

/**
 * Sets the current in-memory auth session.
 * @param {any} session
 */
export function setCurrentAuthSession(session) {
  currentAuthSession = session;
  currentAuthUser = session?.user || null;
}

/**
 * Gets the current in-memory auth user.
 * @returns {any}
 */
export function getCurrentAuthUser() {
  return currentAuthUser;
}

/**
 * Decodes standard JWT payload without external libraries.
 * @param {string} token
 * @returns {any|null}
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (err) {
    console.warn('JWT payload decode failed:', err);
    return null;
  }
}

/**
 * Retrieves persisted auth session from localStorage (checking `sdr_auth_session` and `sb-*-auth-token`).
 * @returns {any|null}
 */
export function getStoredAuthSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SESSION);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.access_token || parsed.user)) return parsed;
    }

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        const item = localStorage.getItem(key);
        if (item) {
          const parsed = JSON.parse(item);
          if (parsed && (parsed.access_token || parsed.user)) return parsed;
        }
      }
    }
  } catch (err) {
    console.warn('getStoredAuthSession read note:', err);
  }
  return null;
}

/**
 * Gets the active JWT access token or falls back to public anon key.
 * @param {string} [fallbackAnonKey]
 * @returns {string}
 */
export function getAuthAccessToken(fallbackAnonKey = SUPABASE_CONFIG.DEFAULT_ANON_KEY) {
  if (currentAuthSession && currentAuthSession.access_token) {
    return currentAuthSession.access_token;
  }
  const stored = getStoredAuthSession();
  if (stored && stored.access_token) {
    return stored.access_token;
  }
  return fallbackAnonKey || '';
}

/**
 * Returns the email of the currently authenticated operator.
 * @returns {string}
 */
export function getAuthenticatedOperatorEmail() {
  return currentAuthSession?.user?.email || currentAuthUser?.email || '';
}

/**
 * Formats and returns the human-readable display name of the active operator.
 * @returns {string}
 */
export function getAuthenticatedOperatorName() {
  const user = currentAuthSession?.user || currentAuthUser;
  if (!user) return 'เจ้าหน้าที่คลัง SDR';

  const meta = user.user_metadata || user.raw_user_meta_data || {};
  const name =
    meta.full_name ||
    meta.name ||
    meta.display_name ||
    meta.user_name ||
    meta.custom_claims?.name ||
    user.name ||
    user.full_name ||
    (user.identities && user.identities[0]?.identity_data?.full_name) ||
    (user.identities && user.identities[0]?.identity_data?.name) ||
    '';

  if (name && String(name).trim() && !String(name).includes('@')) {
    return String(name).trim();
  }

  if (user.email) {
    const rawLocal = String(user.email).split('@')[0];
    const formatted = rawLocal
      .split('.')
      .map(p => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
    return formatted || rawLocal;
  }

  return 'เจ้าหน้าที่คลัง SDR';
}

/**
 * Validates if an email is authorized to access the system based on allowed domain rules.
 * Strictly allowed domains:
 * - @student.mahidol.edu
 * - @student.mahidol.ac.th
 * - @mahidol.edu
 * - @mahidol.ac.th
 * 
 * Strictly rejects lookalike attacks and unauthorized domains.
 *
 * @param {string} email
 * @param {string[]} [customAllowedDomains=null]
 * @returns {boolean}
 */
export function isAuthorizedUser(userOrEmail, customAllowedDomains = null) {
  const email = (typeof userOrEmail === 'object' && userOrEmail !== null)
    ? (userOrEmail.email || '')
    : userOrEmail;
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim().toLowerCase();
  if (!trimmed || !trimmed.includes('@')) return false;

  const parts = trimmed.split('@');
  if (parts.length !== 2) return false;
  const [localPart, domainPart] = parts;
  if (!localPart || !domainPart) return false;

  const allowedList = Array.isArray(customAllowedDomains) && customAllowedDomains.length > 0
    ? customAllowedDomains
    : ALLOWED_AUTH_DOMAINS;

  return allowedList.some(rawAllowed => {
    if (!rawAllowed || typeof rawAllowed !== 'string') return false;
    const cleanAllowed = rawAllowed.trim().toLowerCase().replace(/^@/, '');
    if (!cleanAllowed) return false;

    // Strict exact domain match only
    return domainPart === cleanAllowed;
  });
}

/**
 * Refreshes an expired auth session via Supabase Client refreshSession API.
 * @param {any} client - Supabase client
 * @returns {Promise<any|null>} Refreshed session or null
 */
export function refreshAuthSession(client) {
  return new Promise(async (resolve) => {
    if (!client || !client.auth) return resolve(null);
    try {
      const stored = getStoredAuthSession();
      const rToken = currentAuthSession?.refresh_token || stored?.refresh_token;
      if (!rToken) return resolve(null);

      const { data, error } = await client.auth.refreshSession({ refresh_token: rToken });
      if (data && data.session && data.session.access_token) {
        currentAuthSession = data.session;
        currentAuthUser = data.session.user;
        try {
          localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(data.session));
        } catch (e) {}
        return resolve(data.session);
      }
      return resolve(null);
    } catch (err) {
      console.warn('refreshAuthSession failed:', err);
      return resolve(null);
    }
  });
}

/**
 * Signs in using Google OAuth redirect flow.
 * @param {Object} [options={}]
 * @param {string} [options.supabaseUrl]
 * @param {string} [options.redirectTo]
 */
export function signInWithGoogle(options = {}) {
  const url = options.supabaseUrl || SUPABASE_CONFIG.DEFAULT_URL;
  const redirect = options.redirectTo || (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : '');
  const oauthUrl = `${url}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(redirect)}`;

  if (options.navigate !== false && typeof window !== 'undefined' && window.location) {
    try {
      window.location.href = oauthUrl;
    } catch {
      // Catch JSDOM "Not implemented: navigation" safely
    }
  }
  return oauthUrl;
}

/**
 * Signs out the current user, invalidates tokens, clears session, and broadcasts logout to all tabs.
 * @param {any} [client] - Optional Supabase client
 */
export async function signOutUser(client = null) {
  try {
    const activeClient = client || (typeof window !== 'undefined' ? window.__SDR_SUPABASE_CLIENT : null);
    if (activeClient && activeClient.auth && typeof activeClient.auth.signOut === 'function') {
      await activeClient.auth.signOut().catch(err => console.warn('Supabase client signOut note:', err));
    }
  } catch (err) {
    console.warn('signOut error:', err);
  } finally {
    currentAuthSession = null;
    currentAuthUser = null;
    try {
      localStorage.removeItem(STORAGE_KEYS.SESSION);
      localStorage.removeItem(STORAGE_KEYS.USER);
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
          localStorage.removeItem(key);
        }
      }
    } catch (e) {
      console.warn('LocalStorage clear note:', e);
    }
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }
    } catch (e) {}

    // Broadcast logout to other tabs for Safari WebKit multi-tab sync
    try {
      if (typeof window !== 'undefined') {
        if (typeof BroadcastChannel !== 'undefined') {
          const channel = new BroadcastChannel('sdr_auth_channel');
          channel.postMessage({ type: 'SIGN_OUT', timestamp: Date.now() });
          channel.close();
        }
        localStorage.setItem('sdr_auth_event', JSON.stringify({ type: 'SIGN_OUT', timestamp: Date.now() }));
        localStorage.removeItem('sdr_auth_event');
      }
    } catch (e) {}
  }
}

/**
 * Initializes and recovers authentication state from URL hash, PKCE code, or storage.
 * @param {Object} [options={}]
 * @param {any} [options.client] - Supabase client instance
 * @returns {Promise<{ authenticated: boolean, session: any, user: any, unauthorized?: boolean, email?: string }>}
 */
export async function initAuth(options = {}) {
  const client = options.client || (typeof window !== 'undefined' ? window.__SDR_SUPABASE_CLIENT : null);

  // 1. Instant check for OAuth hash tokens in URL (#access_token=...&refresh_token=...)
  if (typeof window !== 'undefined' && window.location && window.location.hash) {
    const hash = window.location.hash;
    if (hash.includes('access_token=') || hash.includes('token_type=')) {
      try {
        const hashStr = hash.replace(/^#/, '');
        const hashParams = new URLSearchParams(hashStr);
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || '';
        const expiresIn = hashParams.get('expires_in');

        if (accessToken) {
          let user = null;
          const payload = decodeJwtPayload(accessToken);
          if (payload) {
            const rawMeta = payload.user_metadata || payload.raw_user_meta_data || {};
            const userMeta = {
              ...rawMeta,
              avatar_url: rawMeta.avatar_url || rawMeta.picture || payload.avatar_url || payload.picture || '',
              picture: rawMeta.picture || rawMeta.avatar_url || payload.picture || payload.avatar_url || '',
              full_name: rawMeta.full_name || rawMeta.name || payload.full_name || payload.name || '',
              name: rawMeta.name || rawMeta.full_name || payload.name || payload.full_name || ''
            };
            user = {
              id: payload.sub || '',
              email: payload.email || userMeta.email || '',
              user_metadata: userMeta,
              app_metadata: payload.app_metadata || {},
              role: payload.role || 'authenticated'
            };
          } else {
            user = {
              id: 'google-user',
              email: 'member@sdr.club',
              user_metadata: { full_name: 'SDR Member' },
              role: 'authenticated'
            };
          }

          let session = {
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_at: (payload && payload.exp) || (Math.floor(Date.now() / 1000) + (Number(expiresIn) || 3600)),
            expires_in: Number(expiresIn) || 3600,
            token_type: 'bearer',
            user
          };

          const cleanUrl = window.location.pathname + (window.location.search || '');

          if (isAuthorizedUser(user.email)) {
            currentAuthSession = session;
            currentAuthUser = user;
            try {
              localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(session));
              if (window.history && window.history.replaceState) {
                window.history.replaceState(null, document.title, cleanUrl);
              }
            } catch (e) {}

            if (client && client.auth && typeof client.auth.setSession === 'function' && session.access_token) {
              try {
                const { data } = await client.auth.setSession({
                  access_token: session.access_token,
                  refresh_token: session.refresh_token || ''
                });
                if (data && data.user) {
                  user = { ...user, ...data.user };
                  currentAuthUser = user;
                  session = { ...session, user };
                  currentAuthSession = session;
                  try {
                    localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(session));
                    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
                  } catch (e) {}
                }
              } catch (e) {
                console.warn('client setSession note:', e);
              }
            }

            return { authenticated: true, session, user };
          } else {
            console.warn('Unauthorized domain login rejected for:', user.email);
            try {
              localStorage.removeItem(STORAGE_KEYS.SESSION);
              localStorage.removeItem(STORAGE_KEYS.USER);
              if (window.history && window.history.replaceState) {
                window.history.replaceState(null, document.title, cleanUrl);
              }
            } catch (e) {}
            return { authenticated: false, session: null, user: null, unauthorized: true, email: user.email };
          }
        }
      } catch (err) {
        console.warn('OAuth hash parse error:', err);
      }
    }
  }

  // 2. Check for PKCE code in query params (?code=...)
  if (typeof window !== 'undefined' && window.location && window.location.search) {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    if (code && client && client.auth && typeof client.auth.exchangeCodeForSession === 'function') {
      try {
        const { data, error } = await client.auth.exchangeCodeForSession(code);
        if (data && data.session && data.session.user) {
          const cleanUrl = window.location.pathname;
          if (isAuthorizedUser(data.session.user.email)) {
            currentAuthSession = data.session;
            currentAuthUser = data.session.user;
            try {
              localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(data.session));
              if (window.history && window.history.replaceState) {
                window.history.replaceState(null, document.title, cleanUrl);
              }
            } catch (e) {}
            return { authenticated: true, session: data.session, user: data.session.user };
          } else {
            console.warn('Unauthorized domain login rejected for PKCE code:', data.session.user.email);
            try {
              localStorage.removeItem(STORAGE_KEYS.SESSION);
              localStorage.removeItem(STORAGE_KEYS.USER);
              if (window.history && window.history.replaceState) {
                window.history.replaceState(null, document.title, cleanUrl);
              }
            } catch (e) {}
            return { authenticated: false, session: null, user: null, unauthorized: true, email: data.session.user.email };
          }
        }
      } catch (err) {
        console.warn('PKCE exchange error:', err);
      }
    }
  }

  // 3. Check for stored session in localStorage
  const storedSession = getStoredAuthSession();
  if (storedSession && storedSession.user) {
    if (isAuthorizedUser(storedSession.user.email)) {
      currentAuthSession = storedSession;
      currentAuthUser = storedSession.user;

      if (client && client.auth && typeof client.auth.setSession === 'function' && storedSession.access_token) {
        try {
          const { data } = await client.auth.setSession({
            access_token: storedSession.access_token,
            refresh_token: storedSession.refresh_token || ''
          });
          if (data && data.user) {
            storedSession.user = { ...storedSession.user, ...data.user };
            currentAuthUser = storedSession.user;
            currentAuthSession = storedSession;
          }
        } catch (e) {
          console.warn('client setSession storedSession note:', e);
        }
      }

      if (client && client.auth && typeof client.auth.getUser === 'function') {
        try {
          const { data: userData } = await client.auth.getUser();
          if (userData && userData.user) {
            storedSession.user = { ...storedSession.user, ...userData.user };
            currentAuthUser = storedSession.user;
            currentAuthSession = storedSession;
          }
        } catch (e) {
          console.warn('client getUser note:', e);
        }
      }

      try {
        localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(storedSession));
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(storedSession.user));
      } catch (e) {}

      return { authenticated: true, session: storedSession, user: storedSession.user };
    } else {
      // Stored session has unauthorized email, purge immediately
      try {
        localStorage.removeItem(STORAGE_KEYS.SESSION);
        localStorage.removeItem(STORAGE_KEYS.USER);
      } catch (e) {}
    }
  }

  return { authenticated: false, session: null, user: null };
}

